import { t } from '@exodus/i18n-dummy'

import { FIELDS, PRIORITY_LEVELS, VALIDATION_TYPES } from '../constants.js'
import createValidator from './createValidator.js'
import createWrongAddressTypeValidator from './createWrongAddressTypeValidator.js'

// cannot send SOL to token Address
const solanaAddressTypeValidator = createWrongAddressTypeValidator({
  shouldValidate: ({ asset }) => ['solana'].includes(asset.name),
  getMessage: () => t('Destination address is for a different token type.'),
})

// cannot send token X to a different target account address (token Y)
const solanaMintAddressValidator = createValidator({
  id: 'ADDRESS_MINT_MISMATCH',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ asset }) => ['SOLANA_TOKEN'].includes(asset.assetType),

  isValid: async ({ asset, addressDetails }) =>
    !addressDetails.targetMint || asset.mintAddress === addressDetails.targetMint,
  priority: PRIORITY_LEVELS.MIDDLE,
  getMessage: ({ asset }) =>
    t(`Destination Wallet is not a ${asset.baseAsset.displayName} ${asset.displayTicker} address.`),
  field: FIELDS.ADDRESS,
})

const solanaAddressValidator = createValidator({
  id: 'SOLANA_ADDRESS',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ asset }) => asset.name === 'solana',
  isValid: async ({ addressDetails }) => {
    return addressDetails.addressType !== 'token'
  },
  priority: PRIORITY_LEVELS.MIDDLE,
  getMessage: () => t(`The Solana network doesn't allow sending SOL to a Token Account address.`),
  field: FIELDS.ADDRESS,
})

const solanaRentExemptAmountValidator = createValidator({
  id: 'SOL_RENT_EXEMPT_AMOUNT',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ asset, sendAmount }) =>
    sendAmount && (asset.assetType === 'SOLANA_TOKEN' || asset.name === 'solana'),
  isValid: async ({ asset, destinationAddress, sendAmount, baseAssetBalance, feeAmount }) => {
    if (!destinationAddress) {
      return true
    }

    const serverApi = asset.baseAsset.serverApi
    const rentExemptValue = await serverApi.getRentExemptionMinAmount(destinationAddress)
    const rentExemptAmount = asset.baseAsset.currency.baseUnit(rentExemptValue)

    // differentiate between SOL and Solana token
    let isEnoughForRent
    if (asset.name === asset.baseAsset.name) {
      // sending SOL
      isEnoughForRent = sendAmount.gte(rentExemptAmount)
    } else {
      // sending token
      isEnoughForRent = baseAssetBalance
        .sub(feeAmount || asset.feeAsset.currency.ZERO)
        .gte(rentExemptAmount)
    }

    return isEnoughForRent
  },
  priority: PRIORITY_LEVELS.MIDDLE,

  getMessage: () => t(`Amount too low. Send at least 0.002 SOL to cover network fees.`), // hardcoded rent exempt amount, will be refactored once we have a better solution to return async calls

  field: FIELDS.ADDRESS,
})

const solanaRentExemptAmountSenderValidator = {
  id: 'SOL_RENT_EXEMPT_AMOUNT_SENDER',
  type: VALIDATION_TYPES.ERROR,
  priority: PRIORITY_LEVELS.MIDDLE,
  field: FIELDS.ADDRESS,
  shouldValidate: ({ asset, sendAmount, fees, spendableBalance, accountState }) =>
    sendAmount &&
    asset.name === 'solana' &&
    fees &&
    accountState?.rentExemptAmount &&
    spendableBalance,

  isValid: ({ accountState, asset, fromAddress, sendAmount, spendableBalance, fees }) => {
    const rentExemptAmount = accountState.rentExemptAmount
    const remaining = spendableBalance.sub(fees.fee).sub(sendAmount)
    return remaining.isZero || remaining.gte(rentExemptAmount)
  },

  getMessage: ({ accountState, asset }) =>
    t(
      `You can either leave a zero balance, which will close your SOL account, or maintain a minimum balance of ${accountState.rentExemptAmount.toDefaultString()} ${asset.displayTicker} to keep it active.`
    ),
}

const solanaPayValidator = createValidator({
  id: 'SOLANA_PAY',
  type: VALIDATION_TYPES.ERROR,
  shouldValidate: ({ solanaPayInfo }) => {
    return !!(solanaPayInfo?.recipient || solanaPayInfo?.link)
  },
  isValid: async ({ solanaPayInfo }) => {
    return !(solanaPayInfo.recipient || solanaPayInfo.link)
  },
  priority: PRIORITY_LEVELS.MIDDLE,
  getMessage: () => t(`Please use Solana Pay feature to scan this QRCode.`),
  field: FIELDS.ADDRESS,
})

export default [
  solanaAddressTypeValidator,
  solanaAddressValidator,
  solanaMintAddressValidator,
  solanaRentExemptAmountValidator,
  solanaPayValidator,
  solanaRentExemptAmountSenderValidator,
]
